import { ReactNode } from 'react'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: ReactNode
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl'
}

export default function Modal({ isOpen, onClose, title, children, maxWidth = 'xl' }: ModalProps) {
  if (!isOpen) return null

  const maxWidthClass = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
  }[maxWidth]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className={`bg-white rounded-lg shadow-xl ${maxWidthClass} w-full max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-4">
          {children}
        </div>
      </div>
    </div>
  )
}

interface AlertModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  message: string
  type?: 'info' | 'success' | 'error' | 'warning'
}

export function AlertModal({ isOpen, onClose, title, message, type = 'info' }: AlertModalProps) {
  const colors = {
    info: { bg: 'bg-blue-50', text: 'text-blue-800', button: 'bg-blue-600 hover:bg-blue-700' },
    success: { bg: 'bg-green-50', text: 'text-green-800', button: 'bg-green-600 hover:bg-green-700' },
    error: { bg: 'bg-red-50', text: 'text-red-800', button: 'bg-red-600 hover:bg-red-700' },
    warning: { bg: 'bg-yellow-50', text: 'text-yellow-800', button: 'bg-yellow-600 hover:bg-yellow-700' },
  }[type]

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} maxWidth="sm">
      <div className={`${colors.bg} rounded-lg p-4 mb-4`}>
        <p className={`text-sm ${colors.text} whitespace-pre-wrap`}>{message}</p>
      </div>
      <div className="flex justify-end">
        <button
          onClick={onClose}
          className={`px-4 py-2 text-white rounded ${colors.button} transition-colors`}
        >
          OK
        </button>
      </div>
    </Modal>
  )
}

interface ConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  type?: 'danger' | 'warning' | 'info'
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
}: ConfirmModalProps) {
  const colors = {
    danger: { bg: 'bg-red-50', text: 'text-red-800', button: 'bg-red-600 hover:bg-red-700' },
    warning: { bg: 'bg-yellow-50', text: 'text-yellow-800', button: 'bg-yellow-600 hover:bg-yellow-700' },
    info: { bg: 'bg-blue-50', text: 'text-blue-800', button: 'bg-blue-600 hover:bg-blue-700' },
  }[type]

  const handleConfirm = () => {
    onConfirm()
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} maxWidth="sm">
      <div className={`${colors.bg} rounded-lg p-4 mb-4`}>
        <p className={`text-sm ${colors.text} whitespace-pre-wrap`}>{message}</p>
      </div>
      <div className="flex justify-end gap-3">
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
        >
          {cancelText}
        </button>
        <button
          onClick={handleConfirm}
          className={`px-4 py-2 text-white rounded ${colors.button} transition-colors`}
        >
          {confirmText}
        </button>
      </div>
    </Modal>
  )
}
